<template>
	<div>
		<nav class="nav-tabs">
			<ul>
				<li :class="[activeTabName === tab.name ? 'active ' + tab.name : tab.name]" v-for="tab in tabs" v-if="!tab.disabled">
					<a @click.prevent="setActiveTabName(tab.name)" v-if="!tab.disabled"><i :class="tab.icon"></i> {{ tab.displayName }}</a>
				</li>
				<li>
					<a href="https://4choice.atlassian.net/wiki/spaces/PP/pages/2970517513/Shortcodes" target="_blank"><i class="fas fa-code"></i> Shortcodes</a>
				</li><li>
					<a href="https://4choice.atlassian.net/wiki/spaces/PP/pages/2742353929/Content+Style+Guide" target="_blank"><i class="fas fa-file-alt"></i> Content style guide</a>
				</li>
			</ul>
		</nav>

		<div v-if="activeTabName === 'formattingTab'" class="main wg-formatting">
			<div class="page-title">
				<h2>Formatting</h2>
			</div>

			<section class="wg-section">
				<article>
					<h3 id="alerts"><a class="anchor" href="#alerts">#</a> Alerts</h3>
					<div class="alert alert-danger alert--small">
						<div class="alert-content">
							<h4>Alerts are changing!</h4>
							<p>There's new markup associated with alerts with a new, temporary classname. Please use this new markup from now on; once we rewrite the old markup to the new one in the existing articles, we'll replace the <code class="code-inline">aalleerrtt</code> classname with just <code class="code-inline">alert</code> globally.</p>
						</div>
					</div>
					<p>Alerts are special design elements that are meant to grab the attention of the user. It may contain a custom icon, a title and a text. It's fine to use alerts without the icon, or without a supplementary paragraph if you only need the title.</p>
					<p>Requires the base class <code class="code-inline">aalleerrtt</code> and any of the predefined modifier classes to work. Types of predefined alerts (colors):</p>
					<ul class="mb-3">
						<li><code class="code-inline">aalleerrtt--primary</code> &ndash; recommended icon class: <code class="code-inline">icon-info-circle</code></li>
						<li><code class="code-inline">aalleerrtt--success</code> &ndash; recommended icon class: <code class="code-inline">icon-check-circle</code></li>
						<li><code class="code-inline">aalleerrtt--warning</code> &ndash; recommended icon class: <code class="code-inline">icon-question-circle</code></li>
						<li><code class="code-inline">aalleerrtt--danger</code> &ndash; recommended icon class: <code class="code-inline">icon-exclamation-triangle</code></li>
					</ul>
					<pre>/* Fully-fledged alert markup */
&lt;div class="aalleerrtt aalleerrtt--primary"&gt;
	&lt;h4 class="aalleerrtt__title"&gt;Alert title in big bold letters&lt;/h4&gt;
	&lt;p class="aalleerrtt__content"&gt;The main content of the alert goes here.&lt;/p&gt;
	&lt;p class="aalleerrtt__content"&gt;If you want multiple paragraphs, make sure to add the &lt;code class="code-inline"&gt;aalleerrtt__content&lt;/code&gt; classname to each one.&lt;/p&gt;
	&lt;ul class="aalleerrtt__list"&gt;
		&lt;li class="aalleerrtt__list-item"&gt;Now with list items!&lt;/li&gt;
		&lt;li class="aalleerrtt__list-item"&gt;You can have as many as you like.&lt;/li&gt;
	&lt;/ul&gt;
	&lt;div class="aalleerrtt__icon icon-info-circle"&gt;&lt;/div&gt;
&lt;/div&gt;</pre>
					<div class="alert alert-primary alert--small mt-2">
						<div class="alert-content">
							<p>Icon colors are unnecessary to add anymore. Icons will be colored according to the modifier of the parent <code class="code-inline">aalleerrtt</code>.</p>
							<p>From now on just say: <code class="code-inline">&lt;div class="aalleerrtt__icon icon-your-favorite-icon-class"&gt;</code></p>
						</div>
					</div>
					<h4>Alerts without icons</h4>
					<p>If you want an alert without the icon, you should omit the icon's markup and add the <code class="code-inline">aalleerrtt--no-icon</code> modifier class to the parent. This removes the unnecessary padding from inside the alert.</p>
					<pre>&lt;div class="aalleerrtt aalleerrtt--primary aalleerrtt--no-icon"&gt;
	&lt;h4 class="aalleerrtt__title"&gt;Alert title&lt;/h4&gt;
	&lt;p class="aalleerrtt__content"&gt;This alert does not have an icon. Sad, indeed.&lt;/p&gt;
&lt;/div&gt;</pre>
				</article>
				<div class="preview">
					<img :src="imageBase + '/alerts.png'" alt="Alerts">
				</div>
			</section>

			<section class="wg-section">
				<article>
					<h3 id="blockquote"><a class="anchor" href="#blockquote">#</a> Blockquote</h3>
					<p>The default blockquote style. Make sure the content is in a paragraph element.</p>
					<p>Use the <code class="code-inline">blockquote</code> HTML element if you want to show Google this is an actual quote. Alternatively you can just use <code class="code-inline">div class="blockquote"</code> if you want to emphasize a paragraph.</p>
					<p>If the author of the quote is important to show, please use the <strong>Blockquote</strong> on the Shortcodes tab of this page.</p>
					<pre>&lt;blockquote&gt;
	&lt;p&gt;Evisceration golden-red skewered reasonable Sauron the White fortunes market nothing
	sisters kicking. Rabble-rousers kingsfoil woe.&lt;/p&gt;
&lt;/blockquote&gt;</pre>
				</article>
				<div class="preview">
					<img :src="imageBase + '/blockquote-default.png'" alt="Blockquote">
				</div>
			</section>

			<section class="wg-section">
				<article>
					<h3 id="button"><a class="anchor" href="#blockquote">#</a> Button</h3>
					<p>Use button styling on links to direct users' attention to new content, or as a call-to-action.</p>
					<p>Button styling is available in four colors and three sizes. Find these in the Preview section.</p>
					<p><strong>Please note:</strong> Button coloring is not just for show, they have to convey the appropriate meaning that they were designed for. Red denotes danger, yellow means warning, and green reflects success.</p>
					<pre>&lt;a href="#" class="btn btn-md btn-primary"&gt;Welcome, Friend!&lt;/a&gt;
&lt;a href="#" class="btn btn-md btn-danger"&gt;You shall not pass!&lt;/a&gt;</pre>
				</article>
				<div class="preview">
					<table>
						<tbody>
							<tr>
								<td></td>
								<td><code class="code-inline">btn-primary</code></td>
								<td><code class="code-inline">btn-success</code></td>
								<td><code class="code-inline">btn-warning</code></td>
								<td><code class="code-inline">btn-danger</code></td>
							</tr>
							<tr>
								<td><code class="code-inline">btn-sm</code></td>
								<td><span class="btn btn-sm btn-primary">Button</span></td>
								<td><span class="btn btn-sm btn-success">Button</span></td>
								<td><span class="btn btn-sm btn-warning">Button</span></td>
								<td><span class="btn btn-sm btn-danger">Button</span></td>
							</tr>
							<tr>
								<td><code class="code-inline">btn-md</code></td>
								<td><span class="btn btn-md btn-primary">Button</span></td>
								<td><span class="btn btn-md btn-success">Button</span></td>
								<td><span class="btn btn-md btn-warning">Button</span></td>
								<td><span class="btn btn-md btn-danger">Button</span></td>
							</tr>
							<tr>
								<td><code class="code-inline">btn-lg</code></td>
								<td><span class="btn btn-lg btn-primary">Button</span></td>
								<td><span class="btn btn-lg btn-success">Button</span></td>
								<td><span class="btn btn-lg btn-warning">Button</span></td>
								<td><span class="btn btn-lg btn-danger">Button</span></td>
							</tr>
						</tbody>
					</table>
				</div>
			</section>

			<section class="wg-section">
				<article>
					<h3 id="pullquote"><a class="anchor" href="#pullquote">#</a> Pullquote</h3>
					<p>Pullquotes are blocks of quotes floated to either side of the content. Repeat important bits of text from the article copy here. Keep in mind that pullquotes have a fixed width which is way smaller than the full content width, so for optimum readability it is advised not to include larger bodies of text in pullquotes.</p>
					<p>The goal of the pullquote is to catch the content-scanner users' eyes with important bits of content that may just convince them to read the whole section or article.</p>
					<p>Requires the base <code class="code-inline">pullquote</code> class and one of the two classes that define the side it is floated to: <code class="code-inline">pullquote--left</code> or <code class="code-inline">pullquote--right</code>.</p>
					<p>Use the <code class="code-inline">blockquote</code> HTML element if the content is an actual quote, otherwise use the <code class="code-inline">aside</code> HTML element to indicate Google that this is ancillary content.</p>
					<pre>&lt;aside class="pullquote pullquote--right"&gt;
	&lt;p&gt;Key-hole invitations hooded Arkenstone chips push vanishing poisoned kingsfoil parasites wheeled.&lt;/p&gt;
&lt;/aside&gt;</pre>
				</article>
				<div class="preview">
					<img :src="imageBase + '/pullquote.png'" alt="Pullquote">
				</div>
			</section>

			<section class="wg-section">
				<article>
					<h3 id="stat-box"><a class="anchor" href="#stat-box">#</a> Stat Box</h3>
					<p>A special type of pullquote, where the main focus is a certain data point that is emphasised through typography.</p>
					<p>Same rules apply to this as to Pullquotes. The data point should be put in <code class="code-inline">strong</code> tags.</p>
					<p></p>
					<pre>&lt;aside class="stat-box stat-box--left"&gt;
	&lt;p&gt;Cybercrime rates were increased by &lt;strong&gt;64,8%&lt;/strong&gt; last year.&lt;/p&gt;
&lt;/aside&gt;</pre>
				</article>
				<div class="preview">
					<img :src="imageBase + '/stat-box.png'" alt="Stat Box">
				</div>
			</section>

			<section class="wg-section">
				<article>
					<h3 id="impact-statement"><a class="anchor" href="#impact-statement">#</a> Impact Statement</h3>
					<p>The impact statement should primarily be used to emphasise a piece of a sentence better than just regular bold text. We see it commonly used on Hungarian news sites to finish sentence with an impactful statement. Can also be used for full sentences.</p>
					<pre>&lt;p&gt;A number of non-partners took top prizes because &lt;span class="impact-statement"&gt;we genuinely believe they offer a great product.&lt;/span&gt;&lt;/p&gt;

&lt;p&gt;Our Awards were based on &lt;span class="impact-statement"&gt;the most in-depth audit of the industry to date&lt;/span&gt; and the results can be downloaded at the bottom of this page.&lt;/p&gt;</pre>
				</article>
				<div class="preview">
					<img :src="imageBase + '/impact-statement.png'" alt="Impact Statement">
				</div>
			</section>

			<section class="wg-section">
				<article>
					<h3 id="feature-list"><a class="anchor" href="#feature-list">#</a> Feature List</h3>
					<p>Feature lists are special types of unordered lists where the default bullet points are substituted for colored icons. The goal of this styling is to add extra meaning to and aid the understanding of bullet-point lists. You can use feature lists in two ways:</p>
					<p><strong>1.) List block with the same icons for every list item</strong>, e.g. Pros or Cons list:</p>
					<ul>
						<li>The parent class <code class="code-inline">feature-list</code> needs a modifier class that will render the same icon for all list items. These are: <code class="code-inline">feature-list--like</code>, <code class="code-inline">feature-list--dislike</code>, <code class="code-inline">feature-list--question</code>, <code class="code-inline">feature-list--info</code>, and <code class="code-inline">feature-list--danger</code>.</li>
						<li>List items don't require any icon classes.</li>
					</ul>
					<pre class="mt-2 mb-3">&lt;ul class="feature-list feature-list--dislike"&gt;
	&lt;li&gt;[...]&lt;/li&gt;
&lt;/ul&gt;

&lt;ul class="feature-list feature-list--info"&gt;
	&lt;li&gt;[...]&lt;/li&gt;
&lt;/ul&gt;</pre>
					<p><strong>2.) Different icons for every list item in a single unordered list:</strong></p>
					<ul>
						<li>The parent class remains <code class="code-inline">feature-list</code> without the need for any modifier classes.</li>
						<li>All list items get one of the following classnames: <code class="code-inline">like</code>, <code class="code-inline">dislike</code>, <code class="code-inline">question</code>, <code class="code-inline">info</code>, or <code class="code-inline">danger</code>, which will determine the individual icon the list item gets.</li>
					</ul>
					<pre class="mt-2">&lt;ul class="feature-list"&gt;
	&lt;li class="like"&gt;[...]&lt;/li&gt;
	&lt;li class="question"&gt;[...]&lt;/li&gt;
	&lt;li class="danger"&gt;[...]&lt;/li&gt;
&lt;/ul&gt;</pre>
				</article>
				<div class="preview">
					<img :src="imageBase + '/feature-list.png'" alt="Feature List">
				</div>
			</section>

			<section class="wg-section">
				<article>
					<h3 id="steps-list"><a class="anchor" href="#steps-list">#</a> Steps List</h3>
					<p>If you're creating a step-by-step list, you can use <code class="code-inline">ol class="steps-list"</code> to replace the default list numbers with fancier ones.</p>
					<p>If you want to add extra emphasis to the list, you can wrap the <code class="code-inline">ol</code> in a <code class="code-inline">div class="steps-list-container"</code> which replicates a blockquote-like design with lots of whitespace. You can use a subheading inside this <code class="code-inline">div</code>, if necessary.</p>
					<pre>&lt;div class="steps-list-container"&gt;
	&lt;h3&gt;Optional subheading&lt;/h3&gt;
	&lt;ol class="steps-list"&gt;
		&lt;li&gt;[...]&lt;/li&gt;
	&lt;/ol&gt;
&lt;/div&gt;</pre>
				</article>
				<div class="preview">
					<img :src="imageBase + '/steps-list.png'" alt="Steps List">
				</div>
			</section>

			<section class="wg-section">
				<article>
					<h3 id="steps-list-2"><a class="anchor" href="#steps-list-2">#</a> Encapsulated Steps List</h3>
					<p>For longer tutorials, especially where you need to add screenshots or code blocks, you should use the encapsulated version of the Steps List element.</p>
					<p>This version displays every list item in its own card. For this reason, <strong>do not</strong> use the <code class="code-inline">steps-list-container</code> around the list.</p>
					<p>Usage: <code class="code-inline">ol class="steps-list steps-list--blocks"</code></p>
					<pre>&lt;ol class="steps-list steps-list--blocks"&gt;
	&lt;li&gt;[...]&lt;/li&gt;
&lt;/ol&gt;</pre>
				</article>
				<div class="preview">
					<img :src="imageBase + '/steps-list-encapsulated.png'" alt="Encapsulated Steps List">
				</div>
			</section>

			<section class="wg-section">
				<article>
					<h3 id="responsive-tables"><a class="anchor" href="#responsive-tables">#</a> Responsive Tables</h3>
					<p>When creating tables, consider adding a <code class="code-inline">div class="table-responsive"</code> around the table element.</p>
					<p>This makes the table horizontally scrollable when it is wider than the actual viewport.</p>
				</article>
				<pre>
&lt;div class="table-responsive"&gt;
	&lt;table&gt;
		&lt;tbody&gt;
			[...]
		&lt;/tbody&gt;
	&lt;/table&gt;
&lt;/div&gt;</pre>
			</section>

			<section class="wg-section">
				<article>
					<h3 id="responsive-embed"><a class="anchor" href="#responsive-embed">#</a> Responsive Embed</h3>
					<p>When embedding YouTube videos or any kind of third-party iframes into a post, add a <code class="code-inline">div class="embed-responsive"</code> around the iframe. Optionally, you can also add a <code class="code-inline">class="embed-responsive-item"</code> to the item contained inside.</p>
					<p>This will resize the iframe to accommodate the entire width of the available horizontal space, preventing fixed width iframes from protruding on smaller screens.</p>
					<p><strong>Hint:</strong> If you want to embed Youtube videos, please use the <a href="#youtube">Youtube Embed Shortcode</a> which you can find under the <strong>Shortcodes</strong> tab.</p>
				</article>
				<pre>
&lt;div class="embed-responsive"&gt;
	&lt;iframe class="embed-responsive-item" width="560" height="315" src="#"&gt;
	&lt;/iframe&gt;
&lt;/div&gt;</pre>
			</section>

			<section class="wg-section">
				<article>
					<h3 id="multiple-buttons"><a class="anchor" href="#multiple-buttons">#</a> Multiple Buttons</h3>
					<p>If you're placing multiple buttons next to each other, wrap them in a <code class="code-inline">div class="multiple-buttons"</code>.</p>
					<p>This will add a margin around each of the buttons, so no matter the viewport size and how many buttons you add, there will always be adequate space around them.</p>
				</article>
				<pre>
&lt;div class="multiple-buttons"&gt;
	&lt;a class="btn btn-md btn-primary" href="#"&gt;Button 1&lt;/a&gt;
	&lt;a class="btn btn-md btn-primary" href="#"&gt;Button 2&lt;/a&gt;
&lt;/div&gt;</pre>
			</section>
		</div>
	</div>
</template>

<script>
	export default {
		props: {
			dataImageBase: {
				type: String,
			}
		},

		data() {
			return {
				tabs: [
					{
						name: 'formattingTab',
						displayName: 'Formatting',
						icon: 'fas fa-paragraph',
						disabled: false
					},
				],
				activeTabName: null,
				imageBase: this.dataImageBase
			}
		},
		mounted() {
			this.activeTabName = this.tabs[0].name;
		},
		methods: {
			setActiveTabName: function (name) {
				this.activeTabName = name;
			}
		}
	}
</script>
